<script setup>
import { SwButton, SwLoadingDots } from '@getswish/design-library-web';
import { computed, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import InfoCard from '@/_company/components/InfoCard.vue';
import NoCertificates from '@/_company/components/NoCertificates.vue';
import CertTable from '@/_company/components/certTable/CertTable.vue';
import { getPageBySlug } from '@/_company/helpers/contentful';

const store = useStore();
const certificates = computed(() => store.state.company.certificates.certificates);
const user = computed(() => store.state.company.user.user);

const isLoading = ref(false);
const errorMessage = ref('');
const contentBlocks = ref([]);

const certificatesIsEmpty = computed(() => !isLoading.value && !certificates.value.length);
const infoCards = computed(() =>
  contentBlocks.value.filter((block) => block.fields.contentType === 'Info Card'),
);

const route = useRoute();
const getEntries = async () => {
  const slug = route.name;
  contentBlocks.value = await getPageBySlug(slug);
};

const init = async () => {
  getEntries();

  if (!user.value || certificates.value.length) {
    return;
  }

  try {
    isLoading.value = true;
    await store.dispatch('company/certificates/setCertificates');
  } catch (error) {
    errorMessage.value = error.message;
  }

  isLoading.value = false;
};

init();
</script>

<template>
  <div class="content-inner">
    <header>
      <h1>{{ $t('company.certificates.certificates') }}</h1>

      <RouterLink id="create-certificate-button" :to="{ name: 'certificates-create' }">
        <SwButton :label="$t('company.certificates.new')" left-icon="plus-circle" />
      </RouterLink>
    </header>

    <NoCertificates v-if="certificatesIsEmpty" />

    <template v-else>
      <SwLoadingDots v-if="isLoading" color="dark" />

      <p v-else-if="errorMessage">{{ errorMessage }}</p>

      <template v-else>
        <CertTable :certificates="certificates" :showActions="true" />

        <div class="info-card-wrapper" v-if="infoCards">
          <InfoCard v-for="card in infoCards" :key="card.sys.id" :fields="card.fields" />
        </div>
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: $space-24;
  column-gap: $space-24;
}

.info-card-wrapper {
  @include desktop-small {
    display: flex;
  }
}
</style>
